import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { fetchThemeData, getAuth, updateMultiple, uploadThemeImage } from "../adminApi";
import { asArray, notifyError, notifySuccess } from "../adminUtils";
import { BackButton, UploadCard } from "../components";

const MAX_SELECTED = 20;
const MAX_LENGTH = 20;
const SPECIAL_CHARS = /[^A-Z0-9 ]/i;

const normalize = (list) => [...new Set(asArray(list).map((c) => String(c).trim().toUpperCase()).filter(Boolean))];

/**
 * One "Add and manage ... DreamCatchers" card: a multi-select over (full list + custom words)
 * and a field to add a custom word. `keys` are the themesdata targets this card owns.
 */
function CategoryCard({ kind, data, keys, requireSelection, minCount = 0, onSaved, themeName }) {
  const originalFull = normalize(data[keys.full]);
  // the editable word list (default words + custom words); removing / adding changes it until Save
  const [words, setWords] = useState(() => normalize([...asArray(data[keys.full]), ...asArray(data[keys.custom])]));
  const [selected, setSelected] = useState(() => normalize(data[keys.selected]));
  const [newWord, setNewWord] = useState("");
  const [saving, setSaving] = useState(false);

  const toggle = (value) =>
    setSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const removeWord = (value) => {
    setWords((prev) => prev.filter((w) => w !== value));
    setSelected((prev) => prev.filter((w) => w !== value));
  };

  const removeAll = () => {
    setWords([]);
    setSelected([]);
  };

  /** Validates the typed word and returns the list with it added (selected too), or null on a problem. */
  const withNewWord = () => {
    const word = newWord.trim().toUpperCase();
    if (word === "") return { words, selected };
    if (SPECIAL_CHARS.test(word)) return notifyError("Invalid Category", "No special characters allowed.") && null;
    if (word.length > MAX_LENGTH) {
      return notifyError("Length Exceeded", `Maximum Category length of ${MAX_LENGTH} characters reached.`) && null;
    }
    if (words.includes(word)) return notifyError("Duplicate Category", "This category already exists.") && null;
    if (selected.length >= MAX_SELECTED) {
      return notifyError("Cannot add", `Cannot select more than ${MAX_SELECTED} ${kind} words.`) && null;
    }
    return { words: [...words, word], selected: [...selected, word] };
  };

  const addWord = () => {
    const next = withNewWord();
    if (!next) return;
    setWords(next.words);
    setSelected(next.selected);
    setNewWord("");
  };

  const save = async () => {
    const next = withNewWord();
    if (!next) return;

    if (requireSelection && next.selected.length === 0) {
      return notifyError("Selection cannot be empty.", `Please select at least one ${kind} word. `);
    }
    // the game shows this many words of each kind (set on the Settings page), so there must be enough to pick from
    if (next.selected.length < minCount) {
      const missing = minCount - next.selected.length;
      return notifyError(
        `Add more ${kind} words`,
        `The Settings page needs ${minCount} ${kind} word${minCount === 1 ? "" : "s"}, but only ${next.selected.length} ${
          next.selected.length === 1 ? "is" : "are"
        } selected. Please add or select ${missing} more.`
      );
    }
    if (next.selected.length > MAX_SELECTED) {
      return notifyError("Cannot save", `Cannot select more than ${MAX_SELECTED} ${kind} words.`);
    }

    setSaving(true);
    try {
      console.log(`Saving ${kind} words:`, next.selected, "| list:", next.words);
      await updateMultiple(themeName, {
        [keys.selected]: next.selected,
        [keys.full]: next.words.filter((w) => originalFull.includes(w)),
        [keys.custom]: next.words.filter((w) => !originalFull.includes(w)),
      });
      await notifySuccess("Data Updated");
      setNewWord("");
      onSaved();
    } catch (err) {
      notifyError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const Kind = kind[0].toUpperCase() + kind.slice(1);
  return (
    <div className="dc-card">
      <div className="dc-card-head">
        <h4 className="dc-card-title">Add and manage {Kind} DreamCatchers:</h4>
        {words.length > 0 && (
          <button type="button" className="dc-link-btn" onClick={removeAll}>
            Remove all
          </button>
        )}
      </div>
      <p className="dc-bold">Select / Deselect {Kind} DreamCatcher's</p>
      <code>* Use the × to delete a word. After changing the list, click the save button.</code>
      <div className="dc-multiselect">
        {words.length === 0 && <em className="dc-empty">No words yet — add your own below.</em>}
        {words.map((value) => (
          <div key={value} className="dc-check">
            <label>
              <input type="checkbox" checked={selected.includes(value)} onChange={() => toggle(value)} />
              {value}
            </label>
            <button type="button" className="dc-word-x" title={`Remove ${value}`} onClick={() => removeWord(value)}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        ))}
      </div>
      <p className="dc-note">
        <code>
          {selected.length} selected (max {MAX_SELECTED})
        </code>
      </p>

      <h4 className="dc-card-title">Add Custom {Kind} DreamCatcher:</h4>
      <div className="dc-add-word">
        <input
          className="dc-input"
          maxLength={MAX_LENGTH}
          value={newWord}
          onChange={(e) => setNewWord(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addWord();
            }
          }}
        />
        <button type="button" className="dc-btn dc-btn-primary" onClick={addWord}>
          + Add
        </button>
      </div>
      <p className="dc-note">
        <code>* You can add {MAX_LENGTH} title atmost </code>
        <code>* Character limit for the title name is {MAX_LENGTH}</code>
      </p>
      <div className="dc-center">
        <button className={`dc-btn ${kind === "negative" ? "dc-btn-danger" : "dc-btn-success"}`} onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

export default function Content() {
  const { themeName } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(1);
  const [version, setVersion] = useState(0); // remounts the cards after a save so they pick up the stored values

  const load = useCallback(async (remountCards = true) => {
    try {
      const fetched = await fetchThemeData(themeName);
      const auth = getAuth();
      console.log("Admin session:", auth?.sessionId, "| theme:", themeName);
      console.log("Positive words (selected):", fetched.default_cat, `(need ${fetched.positive_upload_count})`);
      console.log("Negative words (selected):", fetched.negative_cat, `(need ${fetched.negtive_upload_count})`);
      setData(fetched);
      if (remountCards) setVersion((v) => v + 1);
    } catch (err) {
      notifyError(err.message);
    }
  }, [themeName]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = (target) => async (file) => {
    try {
      await uploadThemeImage(themeName, target, file);
      await notifySuccess("Successfully Updated");
      load(false); // keep any unsaved word selections in the cards
    } catch (err) {
      notifyError("Upload failed", err.message);
    }
  };

  if (!data) return <div className="dc-page">Loading...</div>;

  return (
    <div className="dc-page">
      <BackButton to={`/admin/rules/${themeName}`} />

      <div className="dc-tabs">
        <button className={`dc-tab ${tab === 1 ? "active" : ""}`} onClick={() => setTab(1)}>
          Add Content
        </button>
        <button className={`dc-tab ${tab === 2 ? "active" : ""}`} onClick={() => setTab(2)}>
          Add Background Images
        </button>
      </div>

      {tab === 1 && (
        <div className="dc-two-col">
          <div>
            <CategoryCard
              key={`pos-${version}`}
              kind="positive"
              themeName={themeName}
              data={data}
              requireSelection
              minCount={Number(data.positive_upload_count) || 0}
              keys={{ full: "full_default_cat", custom: "custom_cat", selected: "default_cat" }}
              onSaved={() => load()}
            />
            <UploadCard
              title="Add Cloud/Thoughts Image backgroud: "
              notes={["Allowed Photo Formats - PNG", "Icon image size must be 534x534"]}
              spec={{ exts: ["png"], width: 534, height: 534 }}
              currentUrl={data.cloud_image}
              previewWidth="45%"
              onUpload={upload("cloud_image")}
            />
          </div>
          <div>
            <CategoryCard
              key={`neg-${version}`}
              kind="negative"
              themeName={themeName}
              data={data}
              requireSelection
              minCount={Number(data.negtive_upload_count) || 0}
              keys={{ full: "full_negative_cat", custom: "custom_negative_cat", selected: "negative_cat" }}
              onSaved={() => load()}
            />
            <UploadCard
              title="Add DreamCatcher Image:"
              notes={["Allowed Photo Formats - PNG", "Icon image size must be 314x679"]}
              spec={{ exts: ["png"], width: 314, height: 679 }}
              currentUrl={data.dreamcatchter_image}
              previewWidth="20%"
              onUpload={upload("dreamcatchter_image")}
            />
          </div>
        </div>
      )}

      {tab === 2 && (
        <div>
          <div className="dc-two-col">
            <UploadCard
              title="Add backgroud image for potrait page"
              notes={["Allowed Photo Formats - PNG, JPG, JPEG", "Icon image size must be 414x896"]}
              spec={{ exts: ["png", "jpg", "jpeg"], width: 414, height: 896 }}
              currentUrl={data.mobile_all_icon_image}
              previewWidth="35%"
              onUpload={upload("mobile_all_icon_image")}
            />
            <UploadCard
              title="Add background image for desktop page: "
              notes={["Allowed Photo Formats - PNG, JPG, JPEG", "Icon image size must be 1920x1080"]}
              spec={{ exts: ["png", "jpg", "jpeg"], width: 1920, height: 1080 }}
              currentUrl={data.all_icon_image}
              previewWidth="100%"
              onUpload={upload("all_icon_image")}
            />
          </div>
        </div>
      )}
    </div>
  );
}
